import { type Screen, type AppStats } from './appTypes';

export const MEMBER_COLORS = ['#8b6fb0', 'var(--color-success)', '#f97316', '#c07088', '#facc15', '#38bdf8', '#8b6fb0', 'var(--color-success)'];
export const MEMBER_EMOJIS_CARE = ['🩺', '💊', '🌿', '🧭', '🏥', '💙', '🫀', '🩻'];
export const MEMBER_EMOJIS_NETWORK = ['💜', '⭐', '🤝', '🌿', '🧭', '💛', '🌟', '🫂'];

export const screenSubtitles: Record<Screen, string> = {
  'home': "Today's snapshot",
  'checkin': "Daily reflection",
  'event-log': "Stigma Tracker",
  'journeys': "Your care contexts",
  'patterns': "Your patterns",
  'articles': "Articles and community wisdom",
  'growth': "Your growth journey",
  'profile': "Profile, privacy & settings",
  'medications': "Your medication list",
  'privacy-policy': "Privacy Policy & Terms",
  'history': "Your full timeline",
  'weather-report': "Your weather insights",
  'hospital-prep': "Hospital prep kit",
  'crisis-plan': "Your crisis plan",
  'care-team': "Your care team",
  'support-network': "Your support network",
  'peer-connect': "The Circle",
  'researcher': "Researcher Dashboard",
  'tools': "Quick access to your care tools",
  'grounding': "Breathing and grounding exercises",
  'moderation': "Moderation Panel",
  'circle-safety': "Filters & safety",
  'moderator': "Moderator Dashboard",
  'crisis-resources': "Crisis support resources",
  'emotions-body-map': "Emotions body map",
  'youth-mockups': "Youth feature previews",
  'content-manager': "Content management",
  'crystal-guide': "Your Crystal Globe",
  'journal': "Your private journal",
};

// edModeConfig iconKey values reference CrystalIconKey from
// artifacts/careweave/src/components/CrystalIcons.tsx. The `icon` emoji
// string is the fallback for renderers that haven't been wired to
// CRYSTAL_ICONS yet (see ACHIEVEMENTS for the same pattern).
export const edModeConfig = {
  off: { label: 'Crisis Mode: Off', icon: '⭕', iconKey: 'close', subtext: 'Tap to plan for a crisis or visit', color: 'var(--color-text-secondary)', bg: 'rgba(75,85,99,0.15)', border: '1px solid rgba(75,85,99,0.4)' },
  plan: { label: 'Crisis Mode: Planning', icon: '📋', iconKey: 'checklist', subtext: 'Preparing for a crisis or visit', color: 'var(--color-warning)', bg: 'rgba(138,96,24,0.15)', border: '1px solid rgba(138,96,24,0.4)' },
  active: { label: 'Crisis Mode: Active', icon: '🚨', iconKey: 'alert-siren', subtext: 'Crisis active — check-ins available', color: '#be785a', bg: 'rgba(190,120,90,0.12)', border: '1px solid rgba(190,120,90,0.45)' },
  reflect: { label: 'Crisis Mode: Reflecting', icon: '🌿', iconKey: 'growth-sprout', subtext: 'Post-crisis reflection', color: '#8b6fb0', bg: 'rgba(139,111,176,0.15)', border: '1px solid rgba(139,111,176,0.4)' },
};

export const BODY_AREA_NAMES: Record<string, string> = {
  head: 'Head',
  'jaw-face': 'Jaw/Face',
  neck: 'Neck',
  'left-shoulder': 'Left Shoulder',
  'right-shoulder': 'Right Shoulder',
  'left-chest': 'Left Chest',
  'right-chest': 'Right Chest',
  'upper-abdomen': 'Upper Abdomen',
  'lower-abdomen': 'Lower Abdomen',
  'pelvis-hips': 'Pelvis/Hips',
  'left-upper-arm': 'Left Upper Arm',
  'right-upper-arm': 'Right Upper Arm',
  'left-forearm': 'Left Forearm',
  'right-forearm': 'Right Forearm',
  'left-hand': 'Left Hand',
  'right-hand': 'Right Hand',
  'left-thigh': 'Left Thigh',
  'right-thigh': 'Right Thigh',
  'left-knee': 'Left Knee',
  'right-knee': 'Right Knee',
  'left-shin': 'Left Shin',
  'right-shin': 'Right Shin',
  'left-foot': 'Left Foot',
  'right-foot': 'Right Foot',
  'back-head': 'Back of Head',
  'back-neck': 'Back of Neck',
  'left-upper-back': 'Left Upper Back',
  'right-upper-back': 'Right Upper Back',
  'left-mid-back': 'Left Mid Back',
  'right-mid-back': 'Right Mid Back',
  'left-lower-back': 'Left Lower Back',
  'right-lower-back': 'Right Lower Back',
  'left-hip-back': 'Left Hip (back)',
  'right-hip-back': 'Right Hip (back)',
  'left-pelvis': 'Left Pelvis / Sacrum',
  'right-pelvis': 'Right Pelvis / Sacrum',
  'left-hamstring': 'Left Hamstring',
  'right-hamstring': 'Right Hamstring',
  'left-calf': 'Left Calf',
  'right-calf': 'Right Calf',
  'left-heel': 'Left Heel',
  'right-heel': 'Right Heel',
  'left-shoulder-back': 'Left Shoulder (back)',
  'right-shoulder-back': 'Right Shoulder (back)',
  'left-upper-arm-back': 'Left Upper Arm (back)',
  'right-upper-arm-back': 'Right Upper Arm (back)',
  'left-forearm-back': 'Left Forearm (back)',
  'right-forearm-back': 'Right Forearm (back)',
  'left-hand-back': 'Left Hand (back)',
  'right-hand-back': 'Right Hand (back)',
  chest: 'Chest/Torso',
  'left-arm': 'Left arm',
  'right-arm': 'Right arm',
  'left-leg': 'Left leg',
  'right-leg': 'Right leg',
  abdomen: 'Abdomen',
  back: 'Back',
};

export const SAFETY_FLAGS = [
  'Pain not being believed', 'Drug-seeking accusation', 'Long wait without explanation',
  'No pain management offered', 'Security called or watching', 'Disrespectful treatment',
  'Race-based comments', 'Age-based dismissal', 'Refused medication', 'Left AMA pressure',
];

export const COMMON_MEDICATIONS = [
  // SCD disease-modifying
  'Hydroxyurea',
  'Voxelotor (Oxbryta)',
  'Crizanlizumab (Adakveo)',
  'L-Glutamine (Endari)',
  'Luspatercept (Reblozyl)',

  // Opioids — VOC / acute pain crisis
  'Morphine',
  'Hydromorphone (Dilaudid)',
  'Oxycodone',
  'Oxycodone + Acetaminophen (Percocet)',
  'Hydrocodone + Acetaminophen (Norco / Vicodin)',
  'Fentanyl',
  'Codeine',
  'Tramadol',
  'Methadone',
  'Buprenorphine (Suboxone)',
  'Buprenorphine patch (Butrans)',

  // Non-opioid pain / anti-inflammatory
  'Acetaminophen (Tylenol)',
  'Ibuprofen (Advil / Motrin)',
  'Naproxen',
  'Ketorolac (Toradol)',
  'Celecoxib (Celebrex)',
  'Diclofenac',
  'Prednisone',

  // Neuropathic / adjuvant pain
  'Gabapentin',
  'Pregabalin (Lyrica)',
  'Amitriptyline',
  'Nortriptyline',
  'Duloxetine (Cymbalta)',

  // Anti-nausea / anti-itch (commonly paired with opioids)
  'Ondansetron (Zofran)',
  'Metoclopramide (Maxeran / Reglan)',
  'Promethazine (Phenergan)',
  'Dimenhydrinate (Gravol)',
  'Diphenhydramine (Benadryl)',

  // Stomach / bowel
  'Omeprazole',
  'Pantoprazole',
  'Senna',
  'Polyethylene glycol (MiraLax)',

  // Antibiotics / prophylaxis
  'Penicillin V',
  'Amoxicillin',
  'Cefdinir',
  'Azithromycin',

  // Iron chelation
  'Deferasirox (Exjade)',
  'Deferoxamine',

  // Blood pressure / pulmonary hypertension
  'Sildenafil',
  'Amlodipine',
  'Lisinopril',
  'Enalapril',

  // Mental health — antidepressants
  'Sertraline (Zoloft)',
  'Fluoxetine (Prozac)',
  'Escitalopram (Lexapro)',
  'Citalopram (Celexa)',
  'Venlafaxine (Effexor)',
  'Bupropion (Wellbutrin)',

  // Mental health — mood stabilizers / antipsychotics
  'Quetiapine (Seroquel)',
  'Aripiprazole (Abilify)',
  'Risperidone',
  'Lithium',

  // Mental health — anxiety / sleep
  'Lorazepam (Ativan)',
  'Clonazepam (Rivotril)',
  'Diazepam (Valium)',
  'Trazodone',
  'Melatonin',

  // ADHD (common in youth)
  'Methylphenidate (Ritalin / Concerta)',
  'Amphetamine / Dextroamphetamine (Adderall)',
  'Atomoxetine (Strattera)',

  // Muscle relaxants
  'Cyclobenzaprine (Flexeril)',
  'Baclofen',

  // Vitamins / supplements
  'Folic Acid',
  'Vitamin D',
  'Magnesium',

  // Other
  'Methotrexate',
  'Loratadine (Claritin)',
];

export const VOC_OPTIONS = [
  { value: 'yes', label: 'Yes — I am in VOC / pain crisis' },
  { value: 'no', label: 'No — monitoring / not in crisis' },
  { value: 'unsure', label: 'Unsure — symptoms building' },
];

export const LEVEL_SYSTEM = [
  { level: 1, name: 'First Thread',        minXP: 0,    maxXP: 200  },
  { level: 2, name: 'Finding the Pattern', minXP: 200,  maxXP: 450  },
  { level: 3, name: 'Threaded Together',   minXP: 450,  maxXP: 750  },
  { level: 4, name: 'Interwoven',          minXP: 750,  maxXP: 1100 },
  { level: 5, name: 'Story Weaver',        minXP: 1100, maxXP: 2100 },
  { level: 6, name: 'Cloth Keeper',        minXP: 2100, maxXP: 3200 },
  { level: 7, name: 'Living Tapestry',     minXP: 3200, maxXP: Infinity },
];

// Achievement iconKey values reference CrystalIconKey from
// artifacts/careweave/src/components/CrystalIcons.tsx. Kept as raw
// strings here (instead of importing the type) so this constants
// file doesn't pull in the renderer module. The App.tsx achievement
// renderers do the CRYSTAL_ICONS[iconKey] dispatch; if iconKey is
// undefined, the emoji string is the fallback.
export const ACHIEVEMENTS = [
  { id: 'first-checkin',    icon: '✅', iconKey: 'check',         label: 'First Thread',        desc: 'You started the weave — your first check-in',    earnedBy: 'Earned by completing your first check-in',                        check: (s: AppStats) => s.totalCheckins >= 1 },
  { id: 'streak-3',         icon: '🔥', iconKey: 'streak',        label: 'Finding Your Rhythm', desc: '3 days of care woven in',                        earnedBy: 'Earned by checking in 3 days in a row',                           check: (s: AppStats) => s.streak >= 3 },
  { id: 'streak-7',         icon: '🏅', iconKey: 'sparkle',       label: 'Woven Week',          desc: '7 days — a whole cloth',                         earnedBy: 'Earned by maintaining a 7-day check-in streak',                   check: (s: AppStats) => s.streak >= 7 },
  { id: 'events-5',         icon: '💜', iconKey: 'heart',         label: 'Five Threads Held',   desc: '5 experiences witnessed and held',               earnedBy: 'Earned by logging 5 events',               check: (s: AppStats) => s.eventsLogged >= 5 },
  { id: 'events-10',        icon: '📋', iconKey: 'log-event',     label: 'Pattern Forming',     desc: '10 experiences — your story taking shape',       earnedBy: 'Earned by logging 10 events',              check: (s: AppStats) => s.eventsLogged >= 10 },
  { id: 'ed-checkin',       icon: '🚨', iconKey: 'crisis-plan',   label: 'Witness Weaver',      desc: 'Documented your truth during a crisis visit',    earnedBy: 'Earned by completing a check-in during a crisis',                 check: (s: AppStats) => s.edCheckins >= 1 },
  { id: 'articles',         icon: '📚', iconKey: 'new-moment',    label: 'Gathering Wisdom',    desc: 'Drew from collective knowledge in Articles',     earnedBy: 'Earned by visiting Articles',                                     check: (s: AppStats) => s.articlesVisited },
  { id: 'ed-visit',         icon: '🏥', iconKey: 'hospital-prep', label: 'Voice in the Room',   desc: 'Advocated for yourself during a crisis',         earnedBy: 'Earned by logging a crisis visit',                                check: (s: AppStats) => s.edVisits >= 1 },
  { id: 'streak-14',        icon: '⚡', iconKey: 'sparkle',       label: 'Continuous Weave',    desc: '14 days — unbroken care, thread by thread',      earnedBy: 'Earned by maintaining a 14-day check-in streak',                  check: (s: AppStats) => s.streak >= 14 },
  { id: 'events-25',        icon: '🗂️', iconKey: 'log-event',     label: 'Story Cloth',         desc: '25 threads woven — a full piece',                earnedBy: 'Earned by logging 25 events',              check: (s: AppStats) => s.eventsLogged >= 25 },
  { id: 'first-goal',       icon: '🌱', iconKey: 'growth-sprout', label: 'Seeds of Growth',     desc: 'Set your first growth goal — the weave expands', earnedBy: 'Earned by adopting your first growth goal',                       check: (s: AppStats) => s.goalsAdopted >= 1 },
  { id: 'first-goal-done',  icon: '🌟', iconKey: 'sparkle',       label: 'Goal Keeper',         desc: 'Completed your first growth goal — fully woven', earnedBy: 'Earned by completing your first growth goal',                     check: (s: AppStats) => s.goalsCompleted >= 1 },
];

export const SPLASH_SEEN_KEY = 'careweave_splash_seen';
export const CAROUSEL_SEEN_KEY = 'careweave_carousel_seen';
export const TUTORIAL_DONE_KEY = 'careweave_tutorial_done';
export const TUTORIAL_VERSION_KEY = 'careweave_tutorial_version';
export const TUTORIAL_XP_KEY = 'careweave_tutorial_xp';

// Welcome-tutorial flags are device-scoped, not user-scoped:
// switching users on the same browser should not re-show the
// onboarding tutorial. The userId parameter is kept for caller
// backwards-compatibility but no longer affects the key.
export function getTutorialDoneKey(_userId?: string | number | null) {
  return TUTORIAL_DONE_KEY;
}
export function getTutorialVersionKey(_userId?: string | number | null) {
  return TUTORIAL_VERSION_KEY;
}
export function getTutorialXpKey(_userId?: string | number | null) {
  return TUTORIAL_XP_KEY;
}
